export const PACKAGING_ITEM_CHANGE_ACTIONS = [
  'item_create',
  'item_update',
  'item_soft_delete',
  'document_add',
  'document_remove',
  'component_material_rematerialize',
  'declaration_of_conformity_download',
  'recyclability_assessment',
  'recyclability_assessment_document_prose',
  'minimisation_assessment',
  'heavy_metals_audit',
  'svhc_audit',
  'field_cross_check',
  'document_classification',
  'manual_override',
] as const

export type PackagingItemChangeAction =
  (typeof PACKAGING_ITEM_CHANGE_ACTIONS)[number]

export const PACKAGING_ITEM_CHANGE_ACTORS = ['ops', 'lex', 'supplier'] as const

export type PackagingItemChangeActor =
  (typeof PACKAGING_ITEM_CHANGE_ACTORS)[number]

export const PACKAGING_ITEM_CHANGE_ENTITY_TYPES = [
  'packaging_item',
  'packaging_component',
  'packaging_item_document',
  'packaged_item_assignment',
] as const

export type PackagingItemChangeEntityType =
  (typeof PACKAGING_ITEM_CHANGE_ENTITY_TYPES)[number]

export type PackagingItemChangeHistoryScalar =
  | string
  | number
  | boolean
  | null

export type PackagingItemChangeHistoryField = {
  entityType: PackagingItemChangeEntityType
  entityId: string
  field: string
  from: PackagingItemChangeHistoryScalar
  to: PackagingItemChangeHistoryScalar
}

export type PackagingItemChangeHistoryEntry = {
  timestamp: string
  actor: PackagingItemChangeActor
  actorUserId?: string
  actorUserDisplayName?: string
  action: PackagingItemChangeAction
  changes: PackagingItemChangeHistoryField[]
  reason?: string
  reasonZh?: string
  correlationId?: string
  reportBasename?: string
}

function isChangeHistoryScalar(value: unknown): value is PackagingItemChangeHistoryScalar {
  return (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  )
}

function parseChangeHistoryField(value: unknown): PackagingItemChangeHistoryField | null {
  if (typeof value !== 'object' || value == null) {
    return null
  }
  const record = value as Record<string, unknown>
  if (
    typeof record.entityId !== 'string' ||
    record.entityId.trim() === '' ||
    typeof record.field !== 'string' ||
    record.field.trim() === ''
  ) {
    return null
  }
  if (
    !PACKAGING_ITEM_CHANGE_ENTITY_TYPES.includes(
      record.entityType as PackagingItemChangeEntityType
    )
  ) {
    return null
  }
  if (!isChangeHistoryScalar(record.from) || !isChangeHistoryScalar(record.to)) {
    return null
  }
  return {
    entityType: record.entityType as PackagingItemChangeEntityType,
    entityId: record.entityId,
    field: record.field,
    from: record.from,
    to: record.to,
  }
}

function parseChangeHistoryEntry(
  value: unknown
): PackagingItemChangeHistoryEntry | null {
  if (typeof value !== 'object' || value == null) {
    return null
  }
  const record = value as Record<string, unknown>
  if (typeof record.timestamp !== 'string' || record.timestamp.trim() === '') {
    return null
  }
  if (
    !PACKAGING_ITEM_CHANGE_ACTORS.includes(
      record.actor as PackagingItemChangeActor
    )
  ) {
    return null
  }
  if (
    !PACKAGING_ITEM_CHANGE_ACTIONS.includes(
      record.action as PackagingItemChangeAction
    )
  ) {
    return null
  }
  if (!Array.isArray(record.changes) || record.changes.length === 0) {
    return null
  }
  const changes: PackagingItemChangeHistoryField[] = []
  for (const change of record.changes) {
    const parsed = parseChangeHistoryField(change)
    if (parsed == null) {
      return null
    }
    changes.push(parsed)
  }
  return {
    timestamp: record.timestamp,
    actor: record.actor as PackagingItemChangeActor,
    action: record.action as PackagingItemChangeAction,
    changes,
    ...(typeof record.actorUserId === 'string' && record.actorUserId.trim() !== ''
      ? { actorUserId: record.actorUserId }
      : {}),
    ...(typeof record.reason === 'string' ? { reason: record.reason } : {}),
    ...(typeof record.reasonZh === 'string' ? { reasonZh: record.reasonZh } : {}),
    ...(typeof record.correlationId === 'string'
      ? { correlationId: record.correlationId }
      : {}),
    ...(typeof record.reportBasename === 'string'
      ? { reportBasename: record.reportBasename }
      : {}),
    ...(typeof record.actorUserDisplayName === 'string' &&
    record.actorUserDisplayName.trim() !== ''
      ? { actorUserDisplayName: record.actorUserDisplayName }
      : {}),
  }
}

export function parsePackagingItemChangeHistory(
  raw: unknown
): PackagingItemChangeHistoryEntry[] {
  if (!Array.isArray(raw)) {
    return []
  }
  const entries: PackagingItemChangeHistoryEntry[] = []
  for (const entry of raw) {
    const parsed = parseChangeHistoryEntry(entry)
    if (parsed != null) {
      entries.push(parsed)
    }
  }
  return entries
}

export function packagingItemChangeHistoryActorLabelKey(
  actor: PackagingItemChangeActor
): string {
  return `itemDetail.changeHistory.actor.${actor}`
}

export function packagingItemChangeHistoryActionLabelKey(
  action: PackagingItemChangeAction
): string {
  return `itemDetail.changeHistory.action.${action}`
}

export function packagingItemChangeHistoryFieldLabelKey(
  field: string
): string | null {
  const knownFields = [
    'name',
    'description',
    'intendedUse',
    'sku',
    'packedItemSku',
    'volumeCm3',
    'packagesOwnProduct',
    'supplierIsManufacturer',
    'manufacturerName',
    'manufacturerAddress',
    'heavyMetalsStatus',
    'substancesOfConcernStatus',
    'completedAt',
    'created',
    'material',
    'weightInGrams',
    'recyclable',
    'recyclingStream',
    'recyclingPct',
    'reusable',
    'notes',
    'added',
    'removed',
    'packagedItemIds',
    'declarationOfConformityDownloaded',
    'declarationNumber',
    'kind',
    'assessmentVersion',
    'verdict',
    'flagged',
    'confidence',
    'inferredDocumentType',
    'documentProseGenerated',
  ] as const
  if (knownFields.includes(field as (typeof knownFields)[number])) {
    return `itemDetail.changeHistory.field.${field}`
  }
  return null
}

export function formatChangeHistoryScalar(
  value: PackagingItemChangeHistoryScalar,
  nullLabel: string
): string {
  if (value === null) {
    return nullLabel
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false'
  }
  return String(value)
}

export function packagingItemChangeHistoryEntryKey(
  entry: PackagingItemChangeHistoryEntry
): string {
  return `${entry.timestamp}:${entry.action}:${entry.actor}:${entry.changes.length}`
}

export function packagingItemChangeHistoryActorUserDisplayName(
  entry: PackagingItemChangeHistoryEntry
): string | null {
  if (
    entry.actorUserDisplayName != null &&
    entry.actorUserDisplayName.trim() !== ''
  ) {
    return entry.actorUserDisplayName.trim()
  }
  return null
}

const MANUAL_OVERRIDE_AUDIT_RESULT_FIELDS = {
  submission_audit_result: 'submission',
  heavy_metals_audit_result: 'heavy_metals',
  svhc_audit_result: 'svhc',
} as const

type ManualOverrideAuditResultField =
  keyof typeof MANUAL_OVERRIDE_AUDIT_RESULT_FIELDS

export function manualOverrideAuditLabelKey(
  field: string
): string | null {
  if (field === 'submission_audit_result') {
    return 'itemDetail.changeHistory.manualOverride.auditSubmission'
  }
  if (field === 'heavy_metals_audit_result') {
    return 'itemDetail.changeHistory.manualOverride.auditHeavyMetals'
  }
  if (field === 'svhc_audit_result') {
    return 'itemDetail.changeHistory.manualOverride.auditSvhc'
  }
  return null
}

export function manualOverrideResultLabelKey(
  field: string,
  value: string
): string | null {
  if (field === 'submission_audit_result') {
    if (value === 'Clear') {
      return 'fieldCrossCheck.clear'
    }
    if (value === 'Flagged') {
      return 'fieldCrossCheck.flagged'
    }
    return null
  }

  if (
    field === 'heavy_metals_audit_result' ||
    field === 'svhc_audit_result'
  ) {
    const statusPrefix =
      field === 'heavy_metals_audit_result'
        ? 'list.heavyMetalsAudit.status.'
        : 'list.svhcAudit.status.'
    if (value === 'Passed') {
      return `${statusPrefix}passed`
    }
    if (value === 'Failed') {
      return `${statusPrefix}failed`
    }
    if (value === 'Review') {
      return `${statusPrefix}flagged`
    }
    return null
  }

  return null
}

export function formatManualOverrideChangeHistorySummary(input: {
  field: ManualOverrideAuditResultField
  from: string
  to: string
  translate: (key: string, options?: Record<string, string>) => string
}): string {
  const auditLabelKey = manualOverrideAuditLabelKey(input.field)
  const fromLabelKey = manualOverrideResultLabelKey(input.field, input.from)
  const toLabelKey = manualOverrideResultLabelKey(input.field, input.to)

  const auditLabel =
    auditLabelKey != null ? input.translate(auditLabelKey) : input.field
  const fromLabel =
    fromLabelKey != null ? input.translate(fromLabelKey) : input.from
  const toLabel = toLabelKey != null ? input.translate(toLabelKey) : input.to

  return input.translate('itemDetail.changeHistory.manualOverride.summary', {
    auditLabel,
    from: fromLabel,
    to: toLabel,
  })
}

export function resolveManualOverrideChangeHistorySummary(
  entry: PackagingItemChangeHistoryEntry
): {
  field: ManualOverrideAuditResultField
  from: string
  to: string
} | null {
  if (entry.action !== 'manual_override' || entry.changes.length === 0) {
    return null
  }
  const change = entry.changes[0]
  if (
    !(change.field in MANUAL_OVERRIDE_AUDIT_RESULT_FIELDS) ||
    typeof change.from !== 'string' ||
    typeof change.to !== 'string'
  ) {
    return null
  }
  return {
    field: change.field as ManualOverrideAuditResultField,
    from: change.from,
    to: change.to,
  }
}
